import { BookOpen, Building2, Plus, Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CarrierCard } from '@/components/carrier-directory/CarrierCard';
import { CreateCarrierModal } from '@/components/carrier-directory/CreateCarrierModal';
import { DivisionHandlesDrawer } from '@/components/carrier-directory/DivisionHandlesDrawer';
import type { ContactHandle } from '@/components/carrier-directory/types';
import { Button } from '@/components/ui/button';
import { useCarriers } from '@/hooks/useCarriers';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

// Loading skeleton for carrier cards
const CarrierCardSkeleton = () => (
  <div className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="h-6 w-48 bg-gray-200 rounded" />
      <div className="h-8 w-24 bg-gray-100 rounded" />
    </div>
    <div className="space-y-3">
      <div className="h-4 w-32 bg-gray-100 rounded" />
      <div className="flex gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-10 flex-1 bg-gray-50 rounded-lg" />
        ))}
      </div>
    </div>
  </div>
);

const CarrierDirectory = () => {
  const { carriers, loading, addHandle, updateHandle, deleteCarrier } =
    useCarriers();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('name') || '',
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCarrierId, setDrawerCarrierId] = useState<string | null>(null);
  const [drawerDivisionId, setDrawerDivisionId] = useState<string | null>(null);
  const [drawerDivisionName, setDrawerDivisionName] = useState<string | null>(
    null,
  );
  const [activeLetter, setActiveLetter] = useState<string | null>(null);

  // Sync search query with URL parameter
  useEffect(() => {
    const nameParam = searchParams.get('name');
    if (nameParam && nameParam !== searchQuery) {
      setSearchQuery(nameParam);
    }
  }, [searchParams]);

  useEffect(() => {
    const handler = (e: CustomEvent) => {
      const { carrierId, divisionId, divisionName } = e.detail || {};
      setDrawerCarrierId(carrierId);
      setDrawerDivisionId(divisionId);
      setDrawerDivisionName(divisionName);
      setDrawerOpen(true);
    };
    window.addEventListener(
      'open-division-drawer' as keyof WindowEventMap,
      handler as EventListener,
    );
    return () =>
      window.removeEventListener(
        'open-division-drawer' as keyof WindowEventMap,
        handler as EventListener,
      );
  }, []);

  // Filter carriers by search
  const filteredCarriers = useMemo(
    () =>
      carriers
        .filter(
          (carrier) =>
            carrier.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            carrier.aliases?.some((alias) =>
              alias.toLowerCase().includes(searchQuery.toLowerCase()),
            ),
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [carriers, searchQuery],
  );

  // Group carriers by first letter
  const carriersByLetter = useMemo(
    () =>
      filteredCarriers.reduce(
        (acc, carrier) => {
          const firstLetter = carrier.name[0].toUpperCase();
          if (!acc[firstLetter]) {
            acc[firstLetter] = [];
          }
          acc[firstLetter].push(carrier);
          return acc;
        },
        {} as Record<string, typeof filteredCarriers>,
      ),
    [filteredCarriers],
  );

  const availableLetters = useMemo(
    () => Object.keys(carriersByLetter).sort(),
    [carriersByLetter],
  );

  // Track scroll position to highlight active letter
  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = document.getElementById('carrier-scroll-area');
      if (!scrollContainer) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const activeLtr = availableLetters.find((letter) => {
        const element = document.getElementById(`letter-${letter}`);
        if (element) {
          const rect = element.getBoundingClientRect();
          return (
            rect.top >= containerRect.top && rect.top <= containerRect.top + 100
          );
        }
        return false;
      });

      if (activeLtr) {
        setActiveLetter(activeLtr);
      }
    };

    const scrollContainer = document.getElementById('carrier-scroll-area');
    scrollContainer?.addEventListener('scroll', handleScroll);
    return () => scrollContainer?.removeEventListener('scroll', handleScroll);
  }, [availableLetters]);

  const scrollToLetter = (letter: string) => {
    const element = document.getElementById(`letter-${letter}`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveLetter(letter);
  };

  const handleAddHandle = async (
    carrierId: string,
    updates: Partial<ContactHandle>,
  ) => {
    await addHandle(
      carrierId,
      updates.handleType || 'PHONE',
      updates.value,
      updates.name,
      updates.description,
      updates.divisionId || null,
      updates.addressLine1,
      updates.addressLine2,
      updates.city,
      updates.state,
      updates.zip,
    );
  };

  const handleUpdateHandle = async (
    handleId: string,
    updates: Partial<ContactHandle>,
  ) => {
    await updateHandle(handleId, updates);
  };

  return (
    <div className="h-screen flex flex-col bg-background-gray">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200">
        <div className="px-8 py-6">
          <div className="max-w-6xl mx-auto">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900 font-sans">
                    Carrier Directory
                  </h1>
                  <p className="text-sm text-gray-500">
                    {loading
                      ? 'Loading...'
                      : `${carriers.length} insurance carriers`}
                  </p>
                </div>
              </div>

              {/* Create Carrier Button */}
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Carrier
              </Button>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or alias..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white outline-none transition-all"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <X className="w-3.5 h-3.5 text-gray-400" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Alphabetical Sidebar */}
        {!searchQuery && availableLetters.length > 0 && (
          <div className="w-12 flex-shrink-0 bg-gray-50/50 border-r border-gray-100 flex flex-col items-center py-2 overflow-y-auto no-scrollbar">
            {ALPHABET.map((letter) => {
              const isAvailable = availableLetters.includes(letter);
              const isActive = activeLetter === letter;
              return (
                <button
                  key={letter}
                  type="button"
                  onClick={() => isAvailable && scrollToLetter(letter)}
                  disabled={!isAvailable}
                  className={`w-8 h-7 text-xs font-medium rounded-md transition-all ${
                    isActive
                      ? 'bg-primary text-white shadow-sm'
                      : isAvailable
                        ? 'text-gray-700 hover:bg-gray-200/70 hover:text-gray-900'
                        : 'text-gray-300 cursor-default'
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        )}

        {/* Scrollable Content */}
        <div
          id="carrier-scroll-area"
          className="flex-1 overflow-y-auto px-8 py-6"
        >
          <div className="max-w-6xl mx-auto">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <CarrierCardSkeleton key={i} />
                ))}
              </div>
            ) : filteredCarriers.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <Building2 className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  No carriers found
                </h3>
                <p className="text-gray-500 mb-4">
                  {searchQuery
                    ? `No results for "${searchQuery}"`
                    : 'Get started by adding your first carrier'}
                </p>
                {searchQuery && (
                  <Button variant="outline" onClick={() => setSearchQuery('')}>
                    Clear search
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-8">
                {availableLetters.map((letter) => (
                  <div key={letter} id={`letter-${letter}`}>
                    <div className="flex items-center gap-3 mb-4 sticky top-0 bg-background-gray py-2 z-10">
                      <span className="text-xl font-bold text-primary">
                        {letter}
                      </span>
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs text-gray-400 font-medium">
                        {carriersByLetter[letter].length}{' '}
                        {carriersByLetter[letter].length === 1
                          ? 'carrier'
                          : 'carriers'}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {carriersByLetter[letter].map((carrier) => (
                        <CarrierCard
                          key={carrier.id}
                          carrier={carrier}
                          onAddHandle={(updates) =>
                            handleAddHandle(carrier.id, updates)
                          }
                          onUpdateHandle={handleUpdateHandle}
                          onDelete={deleteCarrier}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Division Handles Drawer */}
      {drawerOpen &&
        drawerCarrierId &&
        drawerDivisionId &&
        drawerDivisionName && (
          <DivisionHandlesDrawer
            isOpen={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            carrierId={drawerCarrierId}
            divisionId={drawerDivisionId}
            divisionName={drawerDivisionName}
          />
        )}

      {/* Create Carrier Modal */}
      <CreateCarrierModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </div>
  );
};

export default CarrierDirectory;
